/**
 * Tessellated wall mesh builder — straight walls and circular arcs.
 */

import * as pc from "playcanvas";
import { isArcWall, sampleWallPolyline, wallLength, paramOnWall } from "./wall_geometry.js";
import { hasOpeningAsset, instantiateOpening, OPENING_NATIVE } from "./opening_assets.js";

const MIN_SEG = 0.02;
const FRAME_W = 0.06;
/** Walls poke into the ceiling so sun/sky cannot bleed through the joint. */
const WALL_CEIL_OVERLAP = 0.08;
/** Walls extend slightly below the floor plane for the same reason. */
const WALL_FLOOR_OVERLAP = 0.05;
const TRIM_FACE_EPS = 0.002;
/** Pull a butt-jointed wall end back inside the through-wall's mass (see buttJointInset). */
const BUTT_JOINT_INSET = 0.01;

function boxEntity(name, size, material, castShadows = true, receiveShadows = true) {
  const e = new pc.Entity(name);
  e.addComponent("render", { type: "box", material, castShadows, receiveShadows });
  e.setLocalScale(size[0], size[1], size[2]);
  return e;
}

function openingVertical(op, floorY) {
  if (op.type !== "window") {
    const h = op.height ?? 2.1;
    return { bottom: 0, top: h, cy: floorY + h / 2, sill: 0 };
  }
  const h = op.height ?? 1.2;
  const sill = op.sill != null ? op.sill : 0.9;
  return { bottom: sill, top: sill + h, cy: floorY + sill + h / 2, sill };
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * T-junction end-cap retraction. Wall plaster faces sit ON the wall line with
 * the mass behind, so when a partition's endpoint lands on another wall's line
 * (a T-junction), its end cap is a full-height quad exactly coplanar with —
 * and facing the same way as — that wall's visible face. The two quads
 * z-fight, and in baked mode the buried cap carries garbage lightmap texels,
 * flashing dark stripes into the room. Detect that case and report how far to
 * pull the endpoint back so the cap hides inside the through-wall's mass.
 * Opposite-facing coincidences are backface-culled and left alone.
 */
function wallMassContains(px, pz, v) {
  const [ax, az] = v.start ?? [];
  const [bx, bz] = v.end ?? [];
  const tx = bx - ax;
  const tz = bz - az;
  const len = Math.hypot(tx, tz);
  if (!(len >= MIN_SEG)) return false;
  const ux = tx / len;
  const uz = tz / len;
  const along = (px - ax) * ux + (pz - az) * uz;
  if (along < 0 || along > len) return false;
  // Plaster mass spans from the wall line (0) to -thickness along the normal.
  const nvx = Number(v.normal?.[0]) || 0;
  const nvz = Number(v.normal?.[1]) || 0;
  const t = v.thickness ?? 0.15;
  const signed = (px - ax) * nvx + (pz - az) * nvz;
  return signed > -t + 1e-3 && signed < -1e-3;
}

function buttJointInset(px, pz, dx, dz, walls, self) {
  const others = (walls ?? []).filter((v) => v && v.id !== self.id && !v.view && !isArcWall(v));
  let coplanar = false;
  for (const v of others) {
    const nvx = Number(v.normal?.[0]) || 0;
    const nvz = Number(v.normal?.[1]) || 0;
    if (dx * nvx + dz * nvz < 0.9) continue; // cap must face the same way as v's plaster face
    const [ax, az] = v.start ?? [];
    const [bx, bz] = v.end ?? [];
    const tx = bx - ax;
    const tz = bz - az;
    const len = Math.hypot(tx, tz);
    if (!(len >= MIN_SEG)) continue;
    const ux = tx / len;
    const uz = tz / len;
    const rx = px - ax;
    const rz = pz - az;
    if (Math.abs(rx * uz - rz * ux) > 0.01) continue; // endpoint must sit on v's wall line
    const along = rx * ux + rz * uz;
    if (along < -0.01 || along > len + 0.01) continue; // …within v's span
    coplanar = true;
    break;
  }
  if (!coplanar) return 0;
  // If the space just beyond the cap is already solid wall mass (a collinear
  // continuation or a crossing wall), the cap is buried and harmless — leave
  // it alone, since retracting BOTH walls of a mutual joint opens a slit.
  const snx = Number(self.normal?.[0]) || 0;
  const snz = Number(self.normal?.[1]) || 0;
  const halfT = (self.thickness ?? 0.15) / 2;
  const probeX = px - snx * halfT + dx * 0.05;
  const probeZ = pz - snz * halfT + dz * 0.05;
  if (others.some((v) => wallMassContains(probeX, probeZ, v))) return 0;
  return BUTT_JOINT_INSET;
}

/** Shift plaster so its inner face sits on the wall line (avoids corner overlap z-fight). */
function wallMeshOffset(w, thickness) {
  const nx = Number(w.normal?.[0]) || 0;
  const nz = Number(w.normal?.[1]) || 0;
  if (!nx && !nz) return { ox: 0, oz: 0 };
  const half = thickness / 2;
  return { ox: -nx * half, oz: -nz * half };
}

function spawnWindowPanes(room, roomRoot, w, surf, openings) {
  if (w.view) return;
  const floorY = room.floorY ?? 0;
  const useGlb = hasOpeningAsset("window");
  const nat = OPENING_NATIVE.window;

  for (const op of openings.filter((o) => o.type === "window")) {
    const vert = openingVertical(op, floorY);
    const normal = op.normal ?? w.normal ?? [0, 1];
    const nx = Number(normal[0]) || 0;
    const nz = Number(normal[1]) || 0;

    if (useGlb) {
      const mesh = instantiateOpening("window");
      if (mesh) {
        // Native width along local Z — yaw so Z aligns with wall tangent.
        const tx = -nz;
        const tz = nx;
        const yaw = (Math.atan2(tx, tz) * 180) / Math.PI;
        mesh.setLocalEulerAngles(0, yaw, 0);
        mesh.setLocalScale(1, (op.height ?? nat.height) / nat.height, (op.width ?? nat.width) / nat.width);
        mesh.setLocalPosition(op.center[0], floorY + vert.bottom, op.center[2]);
        mesh.name = `window-${op.id}`;
        mesh._aspectKind = "window";
        mesh._aspectOpeningId = op.id;
        roomRoot.addChild(mesh);
        continue;
      }
    }

    const size =
      Math.abs(nz) > Math.abs(nx) ? [op.width, op.height, 0.02] : [0.02, op.height, op.width];
    const pane = boxEntity(`window-${op.id}`, size, surf.glass, false, false);
    pane.setLocalPosition(op.center[0], vert.cy, op.center[2]);
    pane._aspectKind = "window";
    pane._aspectOpeningId = op.id;
    roomRoot.addChild(pane);
  }
}

/** Modern apartment balcony: framed glass, stainless posts, coping, handrail. */
const BALCONY = {
  height: 1.14,
  bay: 0.92,
  post: 0.058,
  glassT: 0.012,
  handW: 0.068,
  handH: 0.042,
  kickH: 0.058,
  kickW: 0.052,
  channel: 0.02,
  outset: 0.06,
  copingW: 0.12,
  copingH: 0.034,
};

function addBalconyPiece(roomRoot, w, name, size, material, x, y, z, yaw, cast = false) {
  const e = boxEntity(name, size, material, cast, false);
  e._aspectKind = "wall";
  e._aspectWallId = w.id;
  e.setLocalPosition(x, y, z);
  if (yaw) e.setLocalEulerAngles(0, yaw, 0);
  roomRoot.addChild(e);
  return e;
}

function balconyOutward(w, room, dist) {
  let nx = Number(w.normal?.[0]) || 0;
  let nz = Number(w.normal?.[1]) || 0;
  if (!nx && !nz && room?.bounds?.min && room?.bounds?.max) {
    const mx = ((Number(w.start?.[0]) || 0) + (Number(w.end?.[0]) || 0)) / 2;
    const mz = ((Number(w.start?.[1]) || 0) + (Number(w.end?.[1]) || 0)) / 2;
    nx = mx - (room.bounds.min[0] + room.bounds.max[0]) / 2;
    nz = mz - (room.bounds.min[2] + room.bounds.max[2]) / 2;
    const n = Math.hypot(nx, nz) || 1;
    return { ox: (nx / n) * dist, oz: (nz / n) * dist };
  }
  const n = Math.hypot(nx, nz) || 1;
  // Authored wall normal faces the room; the rail sits outside.
  return { ox: (-nx / n) * dist, oz: (-nz / n) * dist };
}

function renderModernBalcony(roomRoot, w, floorY, surf, room) {
  const metal = surf.railMetal;
  const glass = surf.balconyGlass ?? surf.glass;
  const copingMat = surf.copingStone ?? surf.baseboard ?? metal;
  if (!metal || !glass) {
    // Old surface cache without the balcony kit — skip rather than draw the glass slab.
    return;
  }
  const { height, bay, post, glassT, handW, handH, kickH, kickW, channel, outset, copingW, copingH } = BALCONY;
  const { ox, oz } = balconyOutward(w, room, outset);
  const { ox: cox, oz: coz } = balconyOutward(w, room, 0.02);
  const copingTop = floorY + 0.02;
  const railY0 = copingTop + 0.004;
  const { points } = sampleWallPolyline(w, 0.45);
  for (let i = 0; i < points.length - 1; i++) {
    const [ax0, az0] = points[i];
    const [bx0, bz0] = points[i + 1];
    const len = Math.hypot(bx0 - ax0, bz0 - az0);
    if (len < MIN_SEG) continue;
    const ux = (bx0 - ax0) / len;
    const uz = (bz0 - az0) / len;
    const yaw = (Math.atan2(ux, uz) * 180) / Math.PI;
    const ax = ax0 + ox;
    const az = az0 + oz;
    const bx = bx0 + ox;
    const bz = bz0 + oz;
    const nBays = Math.max(1, Math.round(len / bay));
    const bayLen = len / nBays;
    const glassW = Math.max(0.18, bayLen - post - 0.04);
    const glassH = Math.max(0.42, height - handH - kickH - 0.06);
    const postH = height - 0.008;

    addBalconyPiece(
      roomRoot,
      w,
      `wall-${w.id}-coping-${i}`,
      [copingW, copingH, len + 0.02],
      copingMat,
      (ax0 + bx0) / 2 + cox,
      copingTop - copingH / 2,
      (az0 + bz0) / 2 + coz,
      yaw,
      true,
    );

    for (let k = 0; k <= nBays; k++) {
      const t = k * bayLen;
      addBalconyPiece(
        roomRoot,
        w,
        `wall-${w.id}-post-${i}-${k}`,
        [post, postH, post],
        metal,
        ax + ux * t,
        railY0 + postH / 2,
        az + uz * t,
        yaw,
        true,
      );
    }

    for (let k = 0; k < nBays; k++) {
      const t = (k + 0.5) * bayLen;
      const gx = ax + ux * t;
      const gz = az + uz * t;
      const gy = railY0 + kickH + 0.018 + glassH / 2;
      addBalconyPiece(roomRoot, w, `wall-${w.id}-glass-${i}-${k}`, [glassT, glassH, glassW], glass, gx, gy, gz, yaw);
      addBalconyPiece(
        roomRoot,
        w,
        `wall-${w.id}-ch-bot-${i}-${k}`,
        [channel, channel, glassW + 0.01],
        metal,
        gx,
        railY0 + kickH + channel / 2,
        gz,
        yaw,
        true,
      );
      addBalconyPiece(
        roomRoot,
        w,
        `wall-${w.id}-ch-top-${i}-${k}`,
        [channel, channel, glassW + 0.01],
        metal,
        gx,
        railY0 + kickH + 0.018 + glassH + channel / 2,
        gz,
        yaw,
        true,
      );
    }

    const mx = (ax + bx) / 2;
    const mz = (az + bz) / 2;
    addBalconyPiece(
      roomRoot,
      w,
      `wall-${w.id}-hand-${i}`,
      [handW, handH, len + 0.02],
      metal,
      mx,
      railY0 + height - handH / 2,
      mz,
      yaw,
      true,
    );
    addBalconyPiece(
      roomRoot,
      w,
      `wall-${w.id}-kick-${i}`,
      [kickW, kickH, len],
      metal,
      mx,
      railY0 + kickH / 2,
      mz,
      yaw,
      true,
    );
  }
}

function isBalconyRoom(r) {
  return /balcon|terrace|loggia/i.test(`${r?.type ?? ""} ${r?.name ?? ""} ${r?.label ?? ""}`);
}

function roomFootprint(r) {
  if (Array.isArray(r?.polygon) && r.polygon.length >= 3) {
    const xs = r.polygon.map((p) => Number(p[0]) || 0);
    const zs = r.polygon.map((p) => Number(p[1]) || 0);
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minZ: Math.min(...zs), maxZ: Math.max(...zs) };
  }
  const x = Number(r?.x) || 0;
  const z = Number(r?.z) || 0;
  return { minX: x, maxX: x + (Number(r?.width) || 0), minZ: z, maxZ: z + (Number(r?.depth) || 0) };
}

function wallOnRoomPerimeter(w, r, eps = 0.22) {
  const b = roomFootprint(r);
  const ax = Number(w.start?.[0]);
  const az = Number(w.start?.[1]);
  const bx = Number(w.end?.[0]);
  const bz = Number(w.end?.[1]);
  if (![ax, az, bx, bz].every(Number.isFinite)) return false;
  const mx = (ax + bx) / 2;
  const mz = (az + bz) / 2;
  const onH =
    Math.abs(az - bz) < eps &&
    (Math.abs(mz - b.minZ) < eps || Math.abs(mz - b.maxZ) < eps) &&
    mx >= b.minX - eps &&
    mx <= b.maxX + eps;
  const onV =
    Math.abs(ax - bx) < eps &&
    (Math.abs(mx - b.minX) < eps || Math.abs(mx - b.maxX) < eps) &&
    mz >= b.minZ - eps &&
    mz <= b.maxZ + eps;
  return onH || onV;
}

function wallOnEnvelope(w, bounds, eps = 0.2) {
  const min = bounds?.min;
  const max = bounds?.max;
  if (!min || !max) return false;
  const ax = Number(w.start?.[0]);
  const az = Number(w.start?.[1]);
  const bx = Number(w.end?.[0]);
  const bz = Number(w.end?.[1]);
  if (Math.abs(az - bz) < eps && (Math.abs(az - min[2]) < eps || Math.abs(az - max[2]) < eps)) return true;
  if (Math.abs(ax - bx) < eps && (Math.abs(ax - min[0]) < eps || Math.abs(ax - max[0]) < eps)) return true;
  return false;
}

/** Restore view rails on balcony exterior walls after a 2D edit drops the flag. */
export function ensureBalconyViewWalls(room) {
  const rooms = room?.rooms ?? [];
  const bals = rooms.filter(isBalconyRoom);
  if (!bals.length || !room?.walls?.length) return false;
  let changed = false;
  for (const w of room.walls) {
    if (!bals.some((r) => wallOnRoomPerimeter(w, r))) continue;
    const exterior = wallOnEnvelope(w, room.bounds) || /^w_(south|north|east|west|ext)/.test(String(w.id ?? ""));
    if (!exterior && !w.view) continue;
    if (!w.view) {
      w.view = true;
      w.viewLength = Number(w.viewLength) || 8;
      changed = true;
    }
  }
  if (changed && Array.isArray(room.openings)) {
    const viewIds = new Set(room.walls.filter((w) => w.view).map((w) => w.id));
    room.openings = room.openings.filter((o) => !(o.type === "window" && viewIds.has(o.wall)));
  }
  return changed;
}

/**
 * Build plaster / frames / glass for one wall (line or arc) into roomRoot.
 */
export function renderWallMeshes(app, room, roomRoot, w, surf, shared = {}) {
  const floorY = room.floorY ?? 0;
  const ceilH = room.dimensions?.height ?? 2.8;
  // Overlap into the ceiling slab — a hairline gap reads as a glowing light seam.
  const wallTop = ceilH + WALL_CEIL_OVERLAP;
  const wallBottom = -WALL_FLOOR_OVERLAP;
  const thickness = w.thickness ?? 0.15;
  // Authored wall heights are measured to the ceiling plane, so the overlap has
  // to be ADDED on top. Clamping to it instead left the wall top exactly
  // coplanar with the ceiling slab, and coplanar faces crack open into a
  // glowing sky hairline along every wall↔ceiling joint. Partial-height
  // partitions stop short of the ceiling, so they take no overlap.
  const authoredH = Math.min(w.height ?? ceilH, ceilH);
  const height = authoredH >= ceilH - MIN_SEG ? ceilH + WALL_CEIL_OVERLAP : authoredH;
  const openings = (room.openings ?? []).filter((o) => o.wall === w.id);

  const renderSegment = (ax, az, bx, bz, segKey, s0, s1, totalLen, offX = 0, offZ = 0) => {
    const dx = bx - ax;
    const dz = bz - az;
    const length = Math.hypot(dx, dz);
    if (length < MIN_SEG) return;

    const ux = dx / length;
    const uz = dz / length;
    const alongZ = Math.abs(dx) < 1e-3;
    const yaw = !alongZ && Math.abs(dz) >= 1e-3 ? (Math.atan2(-dz, dx) * 180) / Math.PI : 0;
    const nx = Number(w.normal?.[0]) || 0;
    const nz = Number(w.normal?.[1]) || 0;

    const ops = openings
      .map((op) => {
        const tc = paramOnWall(w, op.center[0], op.center[2]);
        const dist = tc * totalLen;
        const half = op.width / 2;
        const vert = openingVertical(op, 0);
        const ls = clamp(dist - half, s0, s1) - s0;
        const le = clamp(dist + half, s0, s1) - s0;
        return { op, s: ls, e: le, bottom: vert.bottom, top: vert.top };
      })
      .filter((o) => o.e - o.s > MIN_SEG)
      .sort((a, b) => a.s - b.s);

    const addBox = (u0, u1, y0, y1, material = surf.wall, thick = thickness, castShadows = true, nudgeX = 0, nudgeZ = 0) => {
      const ul = u1 - u0;
      const yl = y1 - y0;
      if (ul < MIN_SEG || yl < MIN_SEG) return;
      const uc = (u0 + u1) / 2;
      const cx = ax + ux * uc + offX + nudgeX;
      const cz = az + uz * uc + offZ + nudgeZ;
      const cy = floorY + (y0 + y1) / 2;
      const size = alongZ ? [thick, yl, ul] : [ul, yl, thick];
      const seg = boxEntity(`wall-${w.id}-${segKey}-${u0.toFixed(2)}-${y0.toFixed(2)}`, size, material, castShadows, true);
      if (material === surf.wall) {
        seg._aspectKind = "wall";
        seg._aspectWallId = w.id;
      }
      seg.setLocalPosition(cx, cy, cz);
      if (yaw) seg.setLocalEulerAngles(0, yaw, 0);
      roomRoot.addChild(seg);
    };

    const faceNudge = TRIM_FACE_EPS;
    const addFrame = (s, e, bottom, top) => {
      addBox(s, s + FRAME_W, bottom, top, surf.frame, thickness + 0.05, false, nx * faceNudge, nz * faceNudge);
      addBox(e - FRAME_W, e, bottom, top, surf.frame, thickness + 0.05, false, nx * faceNudge, nz * faceNudge);
      addBox(s, e, top - FRAME_W, top, surf.frame, thickness + 0.05, false, nx * faceNudge, nz * faceNudge);
      if (bottom > MIN_SEG) addBox(s, e, bottom, bottom + FRAME_W, surf.frame, thickness + 0.05, false, nx * faceNudge, nz * faceNudge);
    };
    const isGlbOpening = (op) =>
      (op.type === "door" && hasOpeningAsset("door")) ||
      (op.type === "window" && hasOpeningAsset("window"));

    if (!ops.length) {
      addBox(0, length, wallBottom, height);
      return;
    }

    let cursor = 0;
    for (const o of ops) {
      if (o.s > cursor + MIN_SEG) {
        addBox(cursor, o.s, wallBottom, height);
      }
      addBox(o.s, o.e, o.top, Math.min(height, wallTop));
      if (o.bottom > MIN_SEG) {
        addBox(o.s, o.e, wallBottom, o.bottom);
      }
      // Unity door/window meshes include their own trim — wall openings stay bare cutouts.
      if (!isGlbOpening(o.op)) {
        addFrame(o.s, o.e, o.bottom, o.top);
      }
      cursor = Math.max(cursor, o.e);
    }
    if (cursor < length - MIN_SEG) {
      addBox(cursor, length, wallBottom, height);
    }
  };

  if (w.view) {
    renderModernBalcony(roomRoot, w, floorY, surf, room);
    return;
  }

  const totalLen = wallLength(w);
  if (!isArcWall(w)) {
    const { ox, oz } = wallMeshOffset(w, thickness);
    const [sx, sz] = w.start;
    const [ex, ez] = w.end;
    const ux = (ex - sx) / totalLen;
    const uz = (ez - sz) / totalLen;
    const walls = room.walls ?? [];
    const insetS = buttJointInset(sx, sz, -ux, -uz, walls, w);
    const insetE = buttJointInset(ex, ez, ux, uz, walls, w);
    renderSegment(
      sx + ux * insetS, sz + uz * insetS,
      ex - ux * insetE, ez - uz * insetE,
      "line", insetS, totalLen - insetE, totalLen, ox, oz,
    );
    spawnWindowPanes(room, roomRoot, w, surf, openings);
    return;
  }

  const { points } = sampleWallPolyline(w, 0.35);
  let acc = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const [ax, az] = points[i];
    const [bx, bz] = points[i + 1];
    const segLen = Math.hypot(bx - ax, bz - az);
    renderSegment(ax, az, bx, bz, `arc${i}`, acc, acc + segLen, totalLen);
    acc += segLen;
  }

  spawnWindowPanes(room, roomRoot, w, surf, openings);
}
